import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "@/i18n/LanguageContext";

const SITE_URL = process.env.REACT_APP_SITE_URL;
const SITE_NAME = "VangardClub";
const OG_LOCALE = { en: "en_US", ru: "ru_RU", uk: "uk_UA" };

/**
 * Single source of truth for per-page metadata: title, description, canonical,
 * robots, Open Graph, Twitter cards and JSON-LD.
 */
export default function Seo({
  title,
  description,
  image,
  type = "website",
  noindex = false,
  schema,
  publishedTime,
}) {
  const { pathname } = useLocation();
  const { lang } = useTranslation();

  const canonical = `${SITE_URL}${pathname === "/" ? "/" : pathname.replace(/\/$/, "")}`;
  const fullTitle = title ? `${title} · ${SITE_NAME}` : SITE_NAME;
  const img = image || `${SITE_URL}/og-image.jpg`;
  const blocks = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1"}
      />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE_NAME} — private members club and concierge`} />
      <meta property="og:locale" content={OG_LOCALE[lang] || OG_LOCALE.en} />
      {Object.entries(OG_LOCALE)
        .filter(([code]) => code !== lang)
        .map(([code, locale]) => (
          <meta key={code} property="og:locale:alternate" content={locale} />
        ))}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={img} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(block)}</script>
      ))}
    </Helmet>
  );
}

export const siteUrl = SITE_URL;

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    alternateName: "Vangard Club",
    url: SITE_URL,
    logo: `${SITE_URL}/logo-light-512.png`,
    image: `${SITE_URL}/og-image.jpg`,
    description:
      "VangardClub is a private members club for founders and business owners, combining a vetted business network with a 24/7 personal concierge.",
    slogan: "Membership that works for you.",
    areaServed: "Worldwide",
    knowsAbout: [
      "private members club",
      "luxury concierge",
      "business networking",
      "entrepreneur community",
    ],
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: SITE_NAME,
    publisher: { "@id": `${SITE_URL}/#organization` },
    inLanguage: ["en", "ru", "uk"],
  };
}

export function breadcrumbSchema(trail) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path === "/" ? "" : item.path}`,
    })),
  };
}
